import { Injectable } from '@angular/core';
import { every } from 'lodash-es';
import { ResponseErrorHandler } from 'asap-team/asap-tools';

import type { FormattedAddress, NormalizedPlaceData } from '@core/types';

@Injectable({ providedIn: 'root' })
export class GooglePlacesService {

  constructor(
    private responseErrorHandler: ResponseErrorHandler,
  ) {}

  remapPlaceAddressComponents(addressComponents: google.maps.places.AddressComponent[]): FormattedAddress {
    const result: any = {};

    const addressByTypes: any[] = addressComponents.map((item: google.maps.places.AddressComponent) => {
      let types: string[] = [...item.types];

      types = types.filter((type: string) => type !== 'political');

      return {
        long_name: item.longText,
        short_name: item.shortText,
        types,
      };
    });

    addressByTypes.forEach((item: any) => {
      result[item.types[0]] = {
        long_name: item.long_name,
        short_name: item.short_name,
      };
    });

    // [TEMP] Related to GMaps bug https://issuetracker.google.com/issues/35830425
    if (result?.administrative_area_level_1?.long_name === 'New York') {
      result.locality = { ...result.administrative_area_level_1 };
    }

    return result;
  }

  validateGooglePlacesAddress(data: FormattedAddress, form: any, errorProcessCallback: Function): boolean {
    const fields: string[] = [
      'country',
      'postal_code',
      'locality',
      'administrative_area_level_3',
      'neighborhood',
      'administrative_area_level_1',
      'street_number',
      'route',
    ];

    const validator = (field: string): boolean => {
      const isPropertyExists = (object: FormattedAddress, property: string): boolean => {
        // City case
        if (['locality', 'administrative_area_level_3', 'neighborhood'].some((v: string) => v === property)) {
          return !!object?.locality || !!object?.administrative_area_level_3 || !!object?.neighborhood;
        }

        return !!object[property];
      };

      const result: boolean = isPropertyExists(data, field);

      if (!result) {
        const prettyFieldName: string = this.prettifyFiledName(field);

        form.controls.address.setErrors({ incorrect: true });

        errorProcessCallback(this.responseErrorHandler.processWithoutControls([
          { source: { pointer: prettyFieldName }, detail: 'is blank or doesn\'t exist' },
        ]));
      }

      return result;

    };

    return every(fields, validator);
  }

  extractPlaceData(placeData: any): NormalizedPlaceData {
    let lat: number = 0;

    if (typeof placeData.lat === 'number') {
      lat = placeData.lat;
    } else if (placeData.location?.lat) {
      lat = typeof placeData.location.lat === 'function' ? placeData.location.lat() : placeData.location.lat;
    }

    let lng: number = 0;

    if (typeof placeData.lng === 'number') {
      lng = placeData.lng;
    } else if (placeData.location?.lng) {
      lng = typeof placeData.location.lng === 'function' ? placeData.location.lng() : placeData.location.lng;
    }

    // Support both new format ('id') and legacy format ('place_id')
    const placeId: string = placeData.id || placeData.place_id || '';

    return {
      lat,
      lng,
      placeId,
    };
  }

  /**
   * Normalizes address components from legacy format to new format.
   * Legacy format uses 'long_name'/'short_name', new format uses 'longText'/'shortText'.
   */
  normalizeAddressComponents(components: any[]): google.maps.places.AddressComponent[] {
    if (!components || !Array.isArray(components)) {
      return [];
    }

    return components.map((component: any) => {
      // Check if already in new format
      if ('longText' in component) {
        return component;
      }

      // Convert from legacy format
      return {
        longText: component.long_name,
        shortText: component.short_name,
        types: component.types || [],
      };
    });
  }

  private prettifyFiledName(data: string): string {
    switch (data) {
      case 'administrative_area_level_1': {
        return 'state';
      }
      case 'locality': {
        return 'city';
      }
      default: {
        return data;
      }
    }
  }

}
